import { useRef, useState } from "react";
import { Loader2, Upload, FileText, Image as ImgIcon, Trash2, ExternalLink, ShieldCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { tutorApi, formatApiError } from "../../lib/tutorApi";

const KIND_LABEL = {
    certificate: "Certificate",
    id_proof: "ID Proof",
    resume: "Resume / CV",
};

/**
 * V25 Phase 2 — Reusable tutor document uploader.
 *
 * Props:
 *  - kind: "certificate" | "id_proof" | "resume"
 *  - documents: array of doc metadata
 *  - onUploaded(doc): called after a successful upload
 *  - onDeleted(): called after a successful delete
 */
export default function TutorFileUpload({ kind, documents = [], onUploaded, onDeleted, testid }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const filtered = documents.filter((d) => d.kind === kind);
    const isSingle = kind === "id_proof" || kind === "resume";
    const showSensitiveNote = kind === "id_proof";

    const pickFile = () => fileRef.current?.click();

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("kind", kind);
        fd.append("file", file);
        setUploading(true);
        try {
            const { data } = await tutorApi.post("/tutor/me/documents", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`${KIND_LABEL[kind]} uploaded`);
            onUploaded?.(data.document);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const del = async (id) => {
        if (!window.confirm("Delete this document?")) return;
        try {
            await tutorApi.delete(`/tutor/me/documents/${id}`);
            toast.success("Deleted");
            onDeleted?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="space-y-2" data-testid={testid}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider inline-flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-slate-400" />
                    {KIND_LABEL[kind]}{!isSingle && " (you can upload multiple)"}
                </span>
                {(!isSingle || filtered.length === 0) && (
                    <button type="button" onClick={pickFile} disabled={uploading}
                        data-testid={`${testid}-pick`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#1E3A8A] hover:bg-[#1E40AF] text-white text-xs font-semibold disabled:opacity-60">
                        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                        Upload
                    </button>
                )}
            </div>
            {showSensitiveNote && (
                <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> ID proof goes to admin for verification before it&rsquo;s linked to your profile.
                </div>
            )}
            <input ref={fileRef} type="file" onChange={upload}
                accept={kind === "resume" ? ".pdf,application/pdf" : ".pdf,image/*"}
                className="hidden" data-testid={`${testid}-input`} />
            <div className="space-y-1">
                {filtered.length === 0 && (
                    <div className="text-xs text-slate-400 italic">No {KIND_LABEL[kind].toLowerCase()} uploaded yet.</div>
                )}
                {filtered.map((d) => {
                    const isImg = (d.mime || "").startsWith("image/");
                    return (
                        <div key={d.id} className="flex items-center gap-2 border border-slate-200 rounded-lg px-2 py-1.5 hover:bg-slate-50"
                            data-testid={`${testid}-row-${d.id}`}>
                            {isImg ? <ImgIcon className="w-4 h-4 text-blue-500 shrink-0" /> : <FileText className="w-4 h-4 text-red-500 shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="text-xs font-medium text-slate-800 truncate">{d.filename}</div>
                                <div className="text-[10px] text-slate-500">{(d.size_bytes / 1024).toFixed(0)} KB · {new Date(d.uploaded_at).toLocaleString()}</div>
                            </div>
                            {d.approved && (
                                <span className="text-[10px] text-emerald-700 inline-flex items-center gap-0.5" title="Approved">
                                    <ShieldCheck className="w-3 h-3" />
                                </span>
                            )}
                            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tutor/me/documents/${d.id}/raw`}
                                target="_blank" rel="noreferrer"
                                onClick={(e) => {
                                    // The /raw endpoint requires auth — we can't pass headers via <a>.
                                    // Use a Blob URL fallback for inline viewing.
                                    e.preventDefault();
                                    tutorApi.get(`/tutor/me/documents/${d.id}/raw`, { responseType: "blob" })
                                        .then((res) => {
                                            const url = URL.createObjectURL(res.data);
                                            window.open(url, "_blank");
                                        })
                                        .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || err.message));
                                }}
                                className="text-slate-400 hover:text-[#1E3A8A]" title="View"
                                data-testid={`${testid}-view-${d.id}`}>
                                <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                            <button type="button" onClick={() => del(d.id)} className="text-slate-400 hover:text-red-600" title="Delete"
                                data-testid={`${testid}-delete-${d.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
